import React, { useReducer, useState, useMemo, useEffect } from 'react';
import {
  HashRouter as Router,
  Switch,
  Route,
  Redirect,
  useLocation
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import compact from 'lodash/compact';
import find from 'lodash/find';
import filter from 'lodash/filter';
import flatten from 'lodash/flatten';
import get from 'lodash/get';
import some from 'lodash/some';

import useFetchProductsBySku from '../../../hooks/useFetchProductsBySku';
import clientOnly from '../../../hoc/clientOnly';
import { StoreMenu } from '../../navigation';
import PackageStep from './steps/PackageStep';
import CustomizeStep from './steps/CustomizeStep';
import ServiceStep from './steps/ServiceStep';
import AddToCart from '../AddToCart';
import Navigation from './Navigation';
import RootStep from './steps/RootStep';

const cdnUrl = `${
  process.env.RAZZLE_MAGENTO_MEDIA_URL
}/catalog/product/600x600`;

const usPackages = [
  {
    sku: 'PKG-FS1-WE-120V',
    name: 'Workflow Essentials'
  },
  {
    sku: 'PKG-FS1-COMPLETE-120V',
    name: 'High Efficiency',
    recommended: true
  },
  {
    sku: 'PKG-FS1-MT-120V',
    name: 'Maximum Throughput'
  },
  {
    sku: 'PKG-FS1-120V',
    name: 'Build Your Own'
  }
];

const euPackages = [
  {
    sku: 'PKG-FS1-WE-230V',
    name: 'Workflow Essentials'
  },
  {
    sku: 'PKG-FS1-COMPLETE-230V',
    name: 'High Efficiency',
    recommended: true
  },
  {
    sku: 'PKG-FS1-MT-230V',
    name: 'Maximum Throughput'
  },
  {
    sku: 'PKG-FS1-230V',
    name: 'Build Your Own'
  }
];

const calculatePriceFromBundleOptions = (
  bundleOptions,
  onlyDefaultPrices = false
) => {
  if (!bundleOptions) {
    return null;
  }

  return bundleOptions.reduce(
    (sum, option) =>
      sum +
      option.product_links.reduce((linkSum, link) => {
        if (onlyDefaultPrices) {
          return linkSum + (link.is_default ? link.price * link.qty : 0);
        }
        return (
          linkSum +
          link.price *
            (link.selectedQty
              ? link.selectedQty * link.qty
              : link.selectedQty === 0
              ? 0
              : link.qty)
        );
      }, 0),
    0
  );
};

// looks and works like redux
// from api data, all packages
const bundlesReducer = (state, action) => {
  if (action.type === 'updateFromData' && action.data) {
    return state.map(bundle => {
      const newBundle = action.data.find(c => c.sku === bundle.sku);
      return {
        ...bundle,
        ...newBundle,
        displayPrice: calculatePriceFromBundleOptions(
          get(newBundle, 'bundle_product_options', null),
          true
        )
      };
    });
  }

  return state;
};

// the mutable options of the selected package
const bundleOptionsReducer = (state, action) => {
  if (action.type === 'init' && action.data) {
    // pick only defaults for initial state
    return compact(
      get(action, 'data.bundle_product_options', []).map(option => {
        if (some(option.product_links, link => link['is_default'])) {
          return option;
        }
        return null;
      })
    );
  }

  if (action.type === 'add' && action.data && action.optionId) {
    const cleanOptions = filter(
      state,
      option => action.optionId !== option['option_id']
    );
    cleanOptions.push(action.data);
    return cleanOptions;
  }

  if (action.type === 'add-radio' && action.data && action.optionId) {
    // Remove all service plans before adding the new one.
    const cleanOptions = get(action, 'remove', null)
      ? filter(state, option => !action.remove.includes(option['option_id']))
      : filter(state, option => action.optionId !== option['option_id']);

    // Filter to get only the selected service plan.
    const updated = {
      ...action.data,
      product_links: action.data.product_links.filter(
        link => link.id === action.id
      )
    };

    cleanOptions.push(updated);

    return cleanOptions;
  }
  if (action.type === 'resetRadio' && action.linkedGroups) {
    const cleanOptions = filter(
      state,
      option => !action.linkedGroups.includes(option.option_id)
    );
    return cleanOptions;
  }

  if (action.type === 'remove' && action.optionId) {
    const newState = filter(
      state,
      option => action.optionId !== option['option_id']
    );

    return newState;
  }
  if (action.type === 'setQty' && action.id && action.optionId && action.qty) {
    const newState = state.map(o => {
      if (o['option_id'] === action.optionId) {
        o['product_links'] = o['product_links'].map(l =>
          l.id === action.id ? { ...l, selectedQty: action.qty } : l
        );
      }

      return o;
    });

    return newState;
  }

  return state;
};

const PackageConfigurator = ({
  additionalPriceText,
  attributes,
  baseUrl,
  category,
  media,
  menu,
  name,
  sku,
  isWholesale = false,
  showAvailability = true,
  storeId
}) => {
  const [t] = useTranslation();
  const [amount] = useState(1); // TODO add setter method when working on step 2
  const availablePackages = ['us_en', 'ca_en'].includes(storeId)
    ? usPackages
    : euPackages;
  const [activeSku, setActiveSku] = useState(availablePackages[0].sku);
  const [price, setPrice] = useState(0);
  const location = useLocation();
  const [bundles, bundlesDispatcher] = useReducer(
    bundlesReducer,
    availablePackages // initial state
  );

  // memo the sku array to avoid multiple fetch calls
  const availableSkus = useMemo(() => availablePackages.map(p => p.sku), [
    availablePackages
  ]);
  const { loading, data } = useFetchProductsBySku(storeId, availableSkus);

  const [bundleOptions, bundleOptionsDispatcher] = useReducer(
    bundleOptionsReducer,
    []
  );

  useEffect(
    () => {
      if (data && data.length > 0) {
        bundlesDispatcher({ type: 'updateFromData', data });
      }
    },
    [data]
  );

  const activeBundle = bundles.find(bundle => bundle.sku === activeSku);

  // only init bundleOptions when package is changed
  useEffect(
    () => {
      if (activeBundle) {
        bundleOptionsDispatcher({ type: 'init', data: activeBundle });
      }
    },
    [activeBundle]
  );

  useEffect(
    () => {
      setPrice(calculatePriceFromBundleOptions(bundleOptions));
    },
    [bundleOptions]
  );
  useEffect(
    () => {
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0;
    },
    [location.hash]
  );

  // get a stable array of active bundle skus
  const activeBundleSkus = useMemo(
    () =>
      flatten(
        get(activeBundle, 'bundle_product_options', []).map(option =>
          option.product_links.map(link => link.sku)
        )
      ),
    [activeBundle]
  );
  //fetch active bundle data
  const {
    loading: activeBundleLoading,
    data: activeBundleData
  } = useFetchProductsBySku(storeId, activeBundleSkus);

  const props = {
    amount,
    attributes,
    baseUrl,
    category,
    media,
    name,
    price,
    sku
  };
  const steps = [
    {
      path: '/package/',
      progressTitle: t('Package'),
      component: (
        <PackageStep
          activeSku={activeSku}
          setActiveSku={setActiveSku}
          availablePackages={availablePackages}
          bundles={bundles}
          bundlesDispatcher={bundlesDispatcher}
          loading={loading}
          image={get(attributes, 'image', null)}
          cdnUrl={cdnUrl}
        />
      ),
      back: '/',
      next: '/customize/'
    },
    {
      path: '/customize/',
      progressTitle: t('Customize'),
      component: (
        <CustomizeStep
          activeSku={activeSku}
          setActiveSku={setActiveSku}
          activeBundle={activeBundle}
          availablePackages={availablePackages}
          bundles={bundles}
          bundlesDispatcher={bundlesDispatcher}
          bundleOptions={bundleOptions}
          bundleOptionsDispatcher={bundleOptionsDispatcher}
          loading={loading}
          cdnUrl={cdnUrl}
          baseUrl={baseUrl}
          activeBundleData={activeBundleData}
          activeBundleLoading={activeBundleLoading}
        />
      ),
      back: '/package/',
      next: '/service/'
    },
    {
      path: '/service/',
      progressTitle: t('Service Plan'),
      component: (
        <ServiceStep
          activeSku={activeSku}
          setActiveSku={setActiveSku}
          activeBundle={activeBundle}
          availablePackages={availablePackages}
          bundles={bundles}
          bundlesDispatcher={bundlesDispatcher}
          bundleOptions={bundleOptions}
          bundleOptionsDispatcher={bundleOptionsDispatcher}
          loading={loading}
          cdnUrl={cdnUrl}
          activeBundleData={activeBundleData}
          activeBundleLoading={activeBundleLoading}
        />
      ),
      back: '/customize/',
      next: (
        <AddToCart
          amount={amount}
          sku={activeSku}
          name={get(activeBundle, 'name', '')}
          title={t('Add to Cart')}
          theBetterBundleOptions={bundleOptions}
          checkoutButtonText={t('Continue to Cart')}
          checkoutModalComment={t(
            'You will be able to generate a quote in Cart.'
          )}
        />
      )
    }
  ];

  return (
    <>
      <StoreMenu baseUrl={baseUrl} menu={menu} isWholesale={isWholesale} />
      <Router>
        <Switch>
          {steps.map((step, i) => (
            <Route key={step.path} path={step.path}>
              {step.component}
              <Navigation
                step={i}
                steps={steps}
                next={step.next}
                back={step.back}
                price={price}
              />
            </Route>
          ))}
          <Route path="/">
            <RootStep
              {...props}
              additionalPriceText={additionalPriceText}
              amount={amount}
              showAvailability={showAvailability}
              price={bundles[3].displayPrice}
            />
          </Route>
          <Route path="*">
            <Redirect to="/" />
          </Route>
        </Switch>
      </Router>
    </>
  );
};

export default clientOnly(PackageConfigurator);
